import { useEffect, useState } from "react";
import { createSupplier, CreateSupplierData, getSupplier, SupplierData, updateSupplier, UpdateSupplierData } from "../remote/suppliers";
import { Button, Form, H4, H6, Input, Label, Spinner, XStack, YStack } from "tamagui";
import { CheckboxWithLabel } from "./CheckboxWithLabel";
import { Check, PlusCircle } from "@tamagui/lucide-icons";
import { Header } from "./Header";
import { useNavigate } from "react-router-dom";

class SupplierEditorProps {
	supplierId?: string;

	constructor(supplierId?: string, createNew?: boolean) {
		this.supplierId = supplierId;
	}
}

const setupFormProperty = (def: any) => {
	const [prop, setter] = useState(def)
	return [prop, setter, (event) => {
		setter(event.target.value)
	}]
}

export const SupplierEditor = (props: SupplierEditorProps) => {
	const navigate = useNavigate();
	const [supplierData, setSupplierData] = useState<SupplierData | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)

	const [name, setName, handleNameInputChange] = setupFormProperty(supplierData?.name ?? '')
	const [email, setEmail, handleEmailInputChange]	= setupFormProperty(supplierData?.email ?? '')
	const [phone, setPhone, handlePhoneInputChange]	= setupFormProperty(supplierData?.phone ?? '')
	const [hosts, setHosts] = useState(supplierData?.hosts ?? [])
	const handleHostsInputChange = (index, event) => {
		let newHosts = hosts.map((x) => x);
		newHosts[index] = event.target.value;
		setHosts(newHosts)
	}

	if (props.supplierId) {
		useEffect(() => {
			if (props.supplierId) {
				getSupplier(props.supplierId, (data, resp) => {
					if (data) {
						setSupplierData(data);
						setName(data.name);
						setEmail(data.email);
						setPhone(data.phone);
						setHosts(data.hosts ?? []);
						setIsLoaded(true);
					} else {
						setSupplierData(null);
						setIsLoaded(false);
					}
				});
			} else {
				setSupplierData(null);
				setIsLoaded(false);
			}
		}, [setSupplierData, setIsLoaded]);
	} else {
		useEffect(() => {
			setSupplierData(new SupplierData("", "", "", ""));
			setIsLoaded(true);
		}, [setSupplierData, setIsLoaded]);
	}

	const [status, setStatus] = useState<'off' | 'submitting' | 'submitted'>('off')
	useEffect(() => {
		if (status === 'submitting') {
			const timer = setTimeout(() => setStatus('off'), 20000)
			return () => {
				clearTimeout(timer)
			}
		}
	}, [status])

	const submitForm = () => {
		if (status == 'submitting') {
			return
		}

		setStatus('submitting')
		if (!props.supplierId) {
			createSupplier(new CreateSupplierData(name, email, phone, hosts), (data, response) => {
				if (response?.status == 200) {
					setStatus('submitted')
					navigate('..', { relative: "path" })
				}
			});
		} else {
			updateSupplier(props.supplierId, new UpdateSupplierData(name, email, phone, hosts), (data, response) => {
				if (response?.status == 200) {
					setStatus('submitted')
					navigate('..', { relative: "path" })
				}
			})
		}
	}

	return ((isLoaded || !props.supplierId) && supplierData) ? (<>
		<Header></Header>
		<YStack flex={1} items="center" justify="center">
			<Form
				alignItems="center"
				minWidth={600}
				gap="$2"
				onSubmit={submitForm}
				borderWidth={1}
				borderRadius="$4"
				backgroundColor="$background"
				borderColor="$borderColor"
				padding="$8"
			>
				{props.supplierId ? (<H4>Edit supplier</H4>) : (<H4>Create a new supplier</H4>)}
				<XStack alignItems="center" gap="$4">
					<Label htmlFor='name' width={120}>Supplier name</Label>
					<Input id='name' value={name ?? ""} onChange={handleNameInputChange} size="$4" borderWidth={2} flex={1} />
				</XStack>
				<XStack alignItems="center" gap="$4">
					<Label htmlFor='email' width={120}>Contact email</Label>
					<Input id='email' value={email ?? ""} onChange={handleEmailInputChange} inputMode='email' size="$4" borderWidth={2} flex={1} />
				</XStack>
				<XStack alignItems="center" gap="$4">
					<Label htmlFor='phone' width={120}>Contact phone</Label>
					<Input id='phone' value={phone ?? ""} onChange={handlePhoneInputChange} inputMode='tel' size="$4" borderWidth={2} flex={1} />
				</XStack>
				<H6 paddingTop="$4">Supplier Hosts</H6>
				{
					hosts.map((x, index) => {
						return (<Input id={`hosts${index}`} key={index} value={x} onChange={(e) => handleHostsInputChange(index, e)} inputMode='text' size="$4" borderWidth={2} flex={1} />)
					})
				}
				<Button iconAfter={PlusCircle} onPress={() => {setHosts(hosts.concat([""]))}}>Add host</Button>
				<Form.Trigger asChild>
					<Button icon={status === 'submitting' ? () => <Spinner /> : status === 'submitted' ? Check : null}>Submit</Button>
				</Form.Trigger>
			</Form>
		</YStack>
	</>) : (<Spinner></Spinner>)
}
